import { ContentCopy, OpenInNew } from '@mui/icons-material';
import {
    styled, Box,
    Button, Snackbar, Alert,
} from '@mui/material';
import { useState } from 'react';
import { CONTENT_MAX_WIDTH } from './constants';

const StickyFooter = styled(Box)(({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: `${theme.spacing(2)} ${theme.spacing(8)}`,
    zIndex: theme.zIndex.appBar - 1,
}));

const FooterContent = styled(Box)(() => ({
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 16,
}));

interface SetsPlaygroundFooterProps {
    galleryUrl: string;
    config: Models.SetsPlayground.SetsConfig | null;
    productKey: string;
}

export const SetsPlaygroundFooter = ({ galleryUrl, config, productKey }: SetsPlaygroundFooterProps): JSX.Element => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    const generateDirectGalleryUrl = (): string => {
        if (!galleryUrl || !config) return '';

        const [baseUrl, existingParams] = galleryUrl.split('?');
        const url = new URL(baseUrl);

        if (existingParams) {
            const params = new URLSearchParams(existingParams);

            params.forEach((value, key) => {
                if (!['rawSets', 'showSets', 'debug', 'noCache'].includes(key)) {
                    url.searchParams.set(key, value);
                }
            });
        }

        // Sets need to be encoded this way for gallery to parse them correctly
        // Each set is added as a separate rawSets parameter so gallery receives an array of JSON strings
        config.sets.forEach((set) => {
            url.searchParams.append('rawSets', JSON.stringify(set));
        });

        url.searchParams.set('showSets', 'true');
        url.searchParams.set('debug', 'true');
        url.searchParams.set('noCache', 'true');

        return url.toString();
    };

    const generatePlaygroundUrl = (): string => {
        if (!galleryUrl || !config || !productKey) {
            return '';
        }
        const url = new URL(`${window.location.origin}/sets-playground`);

        url.searchParams.set('rawSets', encodeURIComponent(JSON.stringify(config.sets)));
        url.searchParams.set('galleryUrl', encodeURIComponent(galleryUrl));
        url.searchParams.set('productKey', encodeURIComponent(productKey));
        return url.toString();
    };

    const handleNavigateToGallery = (): void => {
        const generatedUrl = generateDirectGalleryUrl();

        window.open(generatedUrl, '_blank');
    };

    const handleCopyUrl = async (): Promise<void> => {
        const generatedUrl = generateDirectGalleryUrl();

        await navigator.clipboard.writeText(generatedUrl);
        setSnackbarMessage('Direct gallery URL copied to clipboard');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const handleCopyPlaygroundUrl = async (): Promise<void> => {
        const shareUrl = generatePlaygroundUrl();

        await navigator.clipboard.writeText(shareUrl);
        setSnackbarMessage('Playground URL copied to clipboard');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    return (
        <>
            <StickyFooter>
                <FooterContent>
                    <Button
                        color="secondary"
                        disabled={!galleryUrl || !config?.sets?.length}
                        endIcon={<ContentCopy />}
                        variant="outlined"
                        onClick={handleCopyPlaygroundUrl}
                    >
                        Copy Playground URL
                    </Button>
                    <Button
                        color="secondary"
                        disabled={!galleryUrl || !config?.sets?.length}
                        endIcon={<ContentCopy />}
                        variant="outlined"
                        onClick={handleCopyUrl}
                    >
                        Copy Gallery URL
                    </Button>
                    <Button
                        color="primary"
                        disabled={!galleryUrl || !config?.sets?.length}
                        endIcon={<OpenInNew />}
                        variant="contained"
                        onClick={handleNavigateToGallery}
                    >
                        Go to Gallery
                    </Button>
                </FooterContent>
            </StickyFooter>
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                autoHideDuration={4000}
                open={snackbarOpen}
                onClose={(): void => setSnackbarOpen(false)}
            >
                <Alert severity={snackbarSeverity} onClose={(): void => setSnackbarOpen(false)}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};
